import {
  getStorage,
  ref as firebaseRef,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { app } from "../firebaseConfig";
import { encodeAudioBufferToMp3 } from "../convertToMp3";
import { capitalizeWords, safeJsonParse } from "./productUtils";

const fbStorage = getStorage(app);

const getFirebaseStoragePathFromUrl = (url) => {
  try {
    // Firebase download URL format:
    // https://firebasestorage.googleapis.com/v0/b/BUCKET/o/ENCODED_PATH?alt=media&token=...
    const match = url.match(/\/o\/([^?]+)/);

    if (!match?.[1]) {
      return null;
    }

    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

const getFirebaseRefFromPathOrUrl = (pathOrUrl) => {
  if (!pathOrUrl || typeof pathOrUrl !== "string") {
    return null;
  }

  if (pathOrUrl.startsWith("https://firebasestorage.googleapis.com")) {
    const decodedPath = getFirebaseStoragePathFromUrl(pathOrUrl);

    if (!decodedPath) {
      return null;
    }

    return firebaseRef(fbStorage, decodedPath);
  }

  if (pathOrUrl.startsWith("gs://")) {
    return firebaseRef(fbStorage, pathOrUrl);
  }

  return firebaseRef(fbStorage, pathOrUrl);
};

export const deleteFirebaseFiles = async (filePaths = []) => {
  const uniquePaths = [...new Set(filePaths.filter(Boolean))];

  for (const filePath of uniquePaths) {
    const fileRef = getFirebaseRefFromPathOrUrl(filePath);

    if (!fileRef) {
      console.warn("Invalid Firebase path/url, skipping:", filePath);
      continue;
    }

    try {
      await deleteObject(fileRef);
      console.log("🗑️ Deleted Firebase file:", filePath);
    } catch (error) {
      if (error.code === "storage/object-not-found") {
        console.log("File not found, skipping deletion:", filePath);
      } else {
        console.error("Failed to delete Firebase file:", filePath, error);
      }
    }
  }
};

export const uploadFileToFirebasePath = async (filePath, file) => {
  const fileRef = firebaseRef(fbStorage, filePath);
  await uploadBytes(fileRef, file);

  const url = await getDownloadURL(fileRef);

  return {
    path: filePath,
    url,
  };
};

const readFileAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = reject;

    reader.readAsArrayBuffer(file);
  });
};

export const uploadBeatAudioWithLowPreview = async ({
  audioFile,
  sellerEmail,
  beatName,
  artistName,
}) => {
  const capitalizedBeatName = capitalizeWords(beatName);
  const capitalizedArtistName = capitalizeWords(artistName || "Unknown");

  const audioFileType = audioFile.name.split(".").pop();
  const audioFileName = `${capitalizedBeatName}.${audioFileType}`;

  const originalAudioPath = `beats/${sellerEmail}/${capitalizedArtistName}/${capitalizedBeatName}/${audioFileName}`;
  const convertedAudioPath = `beats/${sellerEmail}/${capitalizedArtistName}/${capitalizedBeatName}/low__${capitalizedBeatName}.mp3`;

  const originalUpload = await uploadFileToFirebasePath(
    originalAudioPath,
    audioFile,
  );

  const arrayBuffer = await readFileAsArrayBuffer(audioFile);

  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  if (audioBuffer.numberOfChannels !== 2) {
    await audioContext.close?.();
    throw new Error("The file must be stereo.");
  }

  const processedBuffer = await encodeAudioBufferToMp3(audioBuffer, 64);

  await audioContext.close?.();

  const lowUpload = await uploadFileToFirebasePath(
    convertedAudioPath,
    processedBuffer,
  );

  return {
    audioUrl: originalUpload.url,
    lowAudioUrl: lowUpload.url,
    audioPath: originalAudioPath,
    lowAudioPath: convertedAudioPath,
  };
};

export const uploadExtraFashionImages = async ({
  fashionPath,
  extraImages,
}) => {
  if (!extraImages?.length) {
    return {
      extraFashionPaths: null,
      extraFashionUrls: null,
    };
  }

  const basePath = fashionPath.replace(/\/[^/]+$/, "");

  const uploadedPaths = [];
  const uploadedUrls = [];

  for (const img of extraImages) {
    const extraPath = `${basePath}/extras/${Date.now()}_${img.name}`;
    const upload = await uploadFileToFirebasePath(extraPath, img);

    uploadedPaths.push(upload.path);
    uploadedUrls.push(upload.url);
  }

  return {
    extraFashionPaths: JSON.stringify(uploadedPaths),
    extraFashionUrls: JSON.stringify(uploadedUrls),
  };
};

export const deleteBeatFilesFromDoc = async (beatDoc) => {
  const paths = [beatDoc.audioPath, beatDoc.lowAudioPath, beatDoc.imagePath];

  const stems = safeJsonParse(beatDoc.stems, []);

  stems.forEach((stem) => {
    if (stem.path) paths.push(stem.path);
  });

  await deleteFirebaseFiles(paths);
};

export const deleteTutorialFilesFromDoc = async (tutorialDoc) => {
  const { videoPaths, thumbnailPaths } = safeJsonParse(
    tutorialDoc.tutorialFilesPaths,
    {
      videoPaths: [],
      thumbnailPaths: [],
    },
  );

  await deleteFirebaseFiles([...(videoPaths || []), ...(thumbnailPaths || [])]);
};

export const deleteFashionFilesFromDoc = async (fashionDoc) => {
  const paths = [];

  if (fashionDoc.fashionPath) {
    paths.push(fashionDoc.fashionPath);
  }

  const extraPaths = safeJsonParse(fashionDoc.extraFashionPaths, []);

  paths.push(...extraPaths);

  await deleteFirebaseFiles(paths);
};

export const uploadProductImageReplacement = async ({
  type,
  itemData,
  newImage,
}) => {
  if (!newImage) return {};

  if (type === "tutorial") {
    const upload = await uploadFileToFirebasePath(
      itemData.thumbnailPath,
      newImage,
    );

    return {
      thumbnailUrl: upload.url,
    };
  }

  if (type === "beat") {
    const upload = await uploadFileToFirebasePath(itemData.imagePath, newImage);

    return {
      imageUrl: upload.url,
    };
  }

  if (type === "fashion") {
    const upload = await uploadFileToFirebasePath(
      itemData.fashionPath,
      newImage,
    );

    return {
      fashionUrl: upload.url,
    };
  }

  return {};
};

export const buildUpdatedBeatStems = async ({
  item,
  beatData,
  editStems,
  sellerEmail,
  toast,
}) => {
  const capitalizedBeatName = capitalizeWords(beatData.beatName);
  const capitalizedArtistName = capitalizeWords(item.artistName || "Unknown");

  const finalStems = [];
  const existingStems = safeJsonParse(item.stems, []);

  const editedNames = editStems.map((stem) => stem.name.trim()).filter(Boolean);

  const deletedStems = existingStems.filter(
    (oldStem) => !editedNames.includes(oldStem.name),
  );

  for (const stem of deletedStems) {
    if (stem.path) {
      try {
        await deleteFirebaseFiles([stem.path]);
        toast.success(`🗑️ Deleted stem: ${stem.name}`);
      } catch {
        toast.error(`❌ Failed to delete stem ${stem.name}`);
      }
    }
  }

  for (const stem of editStems) {
    const trimmedName = stem.name.trim();

    if (!trimmedName) continue;

    if (stem.file) {
      const stemFileType = stem.file.name.split(".").pop();

      const stemFileName = `${trimmedName.replace(
        /\s+/g,
        "_",
      )}.${stemFileType}`;

      const stemPath = `beats/${sellerEmail}/${capitalizedArtistName}/${capitalizedBeatName}/stems/${capitalizedBeatName}_${stemFileName}`;

      const upload = await uploadFileToFirebasePath(stemPath, stem.file);

      finalStems.push({
        name: trimmedName,
        path: stemPath,
        url: upload.url,
      });

      toast.success(`📤 Uploaded new stem: ${trimmedName}`);

      const existing = existingStems.find(
        (existingStem) => existingStem.name === trimmedName,
      );

      if (existing?.path) {
        await deleteFirebaseFiles([existing.path]);
        toast.info(`♻️ Replaced old stem: ${trimmedName}`);
      }
    } else {
      const existing = existingStems.find(
        (existingStem) => existingStem.name === trimmedName,
      );

      if (existing) {
        finalStems.push(existing);
        toast.info(`✅ Retained existing stem: ${trimmedName}`);
      }
    }
  }

  return JSON.stringify(finalStems);
};
export const uploadGeneratedAutoStems = async ({
  beatData,
  generatedStems,
  sellerEmail,
  toast,
}) => {
  const beatName = capitalizeWords(beatData.beatName || "Beat").replace(
    /[^a-zA-Z0-9_-]/g,
    "_",
  );

  const artistName = capitalizeWords(beatData.artistName || "Unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_",
  );

  const existingStems = safeJsonParse(beatData.stems, []);

  const generatedNames = new Set(
    generatedStems.map((stem) => stem.name.toLowerCase()),
  );

  const stemsToDelete = existingStems.filter((stem) =>
    generatedNames.has((stem.name || "").toLowerCase()),
  );

  await deleteFirebaseFiles(
    stemsToDelete.map((stem) => stem.path).filter(Boolean),
  );

  const retainedStems = existingStems.filter(
    (stem) => !generatedNames.has((stem.name || "").toLowerCase()),
  );

  const uploadedStems = [];

  for (const stem of generatedStems) {
    const stemKey = (stem.key || stem.name || "stem")
      .toLowerCase()
      .replace(/[^a-zA-Z0-9_-]/g, "_");

    const fileName = `${beatName}_${artistName}_${stemKey}.mp3`;

    const stemPath = `beats/${sellerEmail}/${artistName}/${beatName}/stems/${fileName}`;

    const upload = await uploadFileToFirebasePath(stemPath, stem.file);

    uploadedStems.push({
      name: stem.name,
      path: stemPath,
      url: upload.url,
      autoCreated: true,
    });

    toast.success(`Uploaded auto stem: ${stem.name}`);
  }

  return JSON.stringify([...retainedStems, ...uploadedStems]);
};
